import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Zap, Lock, CheckCircle, XCircle, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PasswordStrength({ password }) {
  const checks = [
    { label: '8+ characters', pass: password.length >= 8 },
    { label: 'Uppercase letter', pass: /[A-Z]/.test(password) },
    { label: 'Lowercase letter', pass: /[a-z]/.test(password) },
    { label: 'Number', pass: /[0-9]/.test(password) },
  ];
  const strength = checks.filter(c => c.pass).length;
  const colors = ['#EF4444', '#F59E0B', '#F59E0B', '#10B981', '#10B981'];

  if (!password) return null;

  return (
    <div className="mt-3 space-y-2">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="h-1 flex-1 rounded-full transition-colors" style={{ background: i < strength ? colors[strength] : 'rgba(255,255,255,0.1)' }} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1">
        {checks.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs" style={{ color: c.pass ? '#10B981' : '#475569' }}>
            {c.pass ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            {c.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setError(true);
    }
  }, [token]);

  const isPasswordStrong = () => {
    return password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong()) { toast.error('Please create a stronger password'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
      if (msg.includes('expired') || msg.includes('Invalid')) {
        setError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: '#050505' }}>
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <XCircle className="w-8 h-8" style={{ color: '#EF4444' }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Invalid or expired link</h1>
          <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Button onClick={() => navigate('/forgot-password')}
            className="w-full rounded-lg py-5 text-sm font-semibold"
            style={{ background: '#6366F1' }}>
            Request New Reset Link
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: '#050505' }}>
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <CheckCircle className="w-8 h-8" style={{ color: '#10B981' }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Password reset!</h1>
          <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
            Your password has been changed successfully. You can now sign in with your new password.
          </p>
          <Button onClick={() => navigate('/auth')}
            className="w-full rounded-lg py-5 text-sm font-semibold"
            style={{ background: '#6366F1' }}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
        </div>

        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Set new password</h1>
        <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
          Choose a strong password for your account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: '#475569' }}>New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#475569' }} />
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter new password" required
                className="pl-10 py-5 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
            </div>
            <PasswordStrength password={password} />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: '#475569' }}>Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#475569' }} />
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" required
                className="pl-10 py-5 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs mt-2" style={{ color: '#EF4444' }}>Passwords do not match</p>
            )}
            {confirmPassword && password === confirmPassword && confirmPassword.length > 0 && (
              <p className="text-xs mt-2" style={{ color: '#10B981' }}>Passwords match</p>
            )}
          </div>

          <Button type="submit" disabled={isLoading || !isPasswordStrong() || password !== confirmPassword}
            className="w-full rounded-lg py-5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
            style={{ background: '#6366F1', boxShadow: '0 0 15px rgba(99,102,241,0.3)' }}>
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      </div>
    </div>
  );
}
